import {
  Flex,
  Box,
  Button,
  Text,
  Tooltip,
  Select,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import CustomDatePicker from "../CustomDatePicker";
import DebouncedAutocompleteInput, { AutocompleteOption } from "../FinancialShared/DebouncedAutocompleteInput";
import {
  searchPromotoras,
  searchCorbans,
  searchGerentes,
  searchVendedores,
} from "@/services/financial-service/global-state.service";
import { CommissionFilters as ICommissionFilters } from "@/models/commission-report.model";

interface CommissionFiltersProps {
  backgroundStyle: any;
  dateRange: [Date | null, Date | null];
  onDateChange: (dates: [Date | null, Date | null]) => void;
  filters: ICommissionFilters;
  onFilterChange: (filters: ICommissionFilters) => void;
  vision: string;
  onVisionChange: (vision: string) => void;
  selectedCorban: AutocompleteOption | null;
  onCorbanSelect: (option: AutocompleteOption | null) => void;
  selectedPromotora: AutocompleteOption | null;
  onPromotoraSelect: (option: AutocompleteOption | null) => void;
  selectedGerente: AutocompleteOption | null;
  onGerenteSelect: (option: AutocompleteOption | null) => void;
  selectedVendedor: AutocompleteOption | null;
  onVendedorSelect: (option: AutocompleteOption | null) => void;
  onQuery: () => void;
  onClear: () => void;
  onDownloadB2C: () => void;
  onDownloadB2B: () => void;
  onDownloadKam: () => void;
  isQuerying: boolean;
  isDownloadingB2C: boolean;
  isDownloadingB2B: boolean;
  isDownloadingKam: boolean;
  hasQueried: boolean;
  setHasQueried: (value: boolean) => void;
  setData: (data: any[]) => void;
}

export default function CommissionFilters({
  backgroundStyle,
  dateRange,
  onDateChange,
  filters,
  onFilterChange,
  vision,
  onVisionChange,
  selectedCorban,
  onCorbanSelect,
  selectedPromotora,
  onPromotoraSelect,
  selectedGerente,
  onGerenteSelect,
  selectedVendedor,
  onVendedorSelect,
  onQuery,
  onClear,
  onDownloadB2C,
  onDownloadB2B,
  onDownloadKam,
  isQuerying,
  isDownloadingB2C,
  isDownloadingB2B,
  isDownloadingKam,
  hasQueried,
  setHasQueried,
  setData,
}: CommissionFiltersProps) {
  const [promoterInput, setPromoterInput] = useState(selectedPromotora?.label ?? "");
  const [corbanInput, setCorbanInput] = useState(selectedCorban?.label ?? "");
  const [gerenteInput, setGerenteInput] = useState(selectedGerente?.label ?? "");
  const [vendedorInput, setVendedorInput] = useState(selectedVendedor?.label ?? "");

  // Sincroniza campos se o estado externo mudar (ex: ao limpar filtros)
  useEffect(() => {
    setPromoterInput(selectedPromotora?.label ?? "");
    setCorbanInput(selectedCorban?.label ?? "");
    setGerenteInput(selectedGerente?.label ?? "");
    setVendedorInput(selectedVendedor?.label ?? "");
  }, [selectedPromotora, selectedCorban, selectedVendedor, selectedGerente]);
  return (
    <Box
      {...backgroundStyle}
      borderRadius="xl"
      p={{ base: 4, md: 6 }}
      color="white"
      boxShadow="lg"
      width="100%"
      minW="0"
      mb={4}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={6} flexWrap="wrap" gap={4}>
        <Text fontSize="xl" fontWeight="bold" fontFamily="Lexend Deca">
          FILTROS DE COMISSÃO FGTS
        </Text>
        <Flex gap={3}>
          <Button
            color="white"
            colorScheme="green"
            textTransform="uppercase"
            leftIcon={<FaFileExcel />}
            onClick={onDownloadB2C}
            isLoading={isDownloadingB2C}
            isDisabled={isDownloadingB2B || isDownloadingKam}
            borderRadius="lg"
            height="2.8rem"
            px={6}
            boxShadow="sm"
            fontSize="sm"
          >
            B2C
          </Button>
          <Button
            color="white"
            colorScheme="green"
            textTransform="uppercase"
            leftIcon={<FaFileExcel />}
            onClick={onDownloadB2B}
            isLoading={isDownloadingB2B}
            isDisabled={isDownloadingB2C || isDownloadingKam}
            borderRadius="lg"
            height="2.8rem"
            px={6}
            boxShadow="sm"
            fontSize="sm"
          >
            B2B
          </Button>
          <Button
            color="white"
            colorScheme="green"
            textTransform="uppercase"
            leftIcon={<FaFileExcel />}
            onClick={onDownloadKam}
            isLoading={isDownloadingKam}
            isDisabled={isDownloadingB2C || isDownloadingB2B}
            borderRadius="lg"
            height="2.8rem"
            px={6}
            boxShadow="sm"
            fontSize="sm"
          >
            KAM
          </Button>
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap={6}>
        <Flex gap={4} flexWrap="wrap" alignItems="flex-end">
          <Box flex={1} minW="260px">
            <Tooltip label="Filtrar por intervalo de datas" fontSize="md" placement="top">
              <CustomDatePicker
                dateRange={dateRange}
                onChange={(dates) => {
                  onDateChange(dates);
                  setHasQueried(false);
                }}
              />
            </Tooltip>
          </Box>

          <Box flex={1} maxW="200px" minW="150px">
            <Tooltip label="Tipo do relatório" fontSize="md" placement="top">
              <Select
                bg="white"
                color={filters.type ? "black" : "gray.500"}
                height="3rem"
                borderRadius="lg"
                fontFamily="Lexend Deca"
                placeholder="selecionar tipo"
                value={filters.type}
                onChange={(e) => {
                  const newType = e.target.value as "b2b" | "b2c" | "kam";
                  onFilterChange({ ...filters, type: newType });

                  // Set default vision values
                  if (newType === "b2b") {
                    onVisionChange("bluepay");
                  } else if (newType === "kam") {
                    onVisionChange("all");
                  } else {
                    onVisionChange("");
                  }

                  setData([]);
                  setHasQueried(false);
                }}
              >
                <option value="b2c">B2C</option>
                <option value="b2b">B2B</option>
                <option value="kam">KAM</option>
              </Select>
            </Tooltip>
          </Box>

          {(filters.type === "b2b" || filters.type === "kam") && (
            <Box flex={1} maxW="250px" minW="200px">
              <Tooltip label={filters.type === "b2b" ? "Relatório filtrado pela forma de pagamento" : "Filtrar por visão (B2B/B2C)"} fontSize="md" placement="top">
                <Select
                  bg="white"
                  color={vision ? "black" : "gray.500"}
                  height="3rem"
                  borderRadius="lg"
                  fontFamily="Lexend Deca"
                  placeholder={filters.type === "b2b" ? "Forma de pagamento" : "Selecionar visão"}
                  value={vision}
                  onChange={(e) => {
                    onVisionChange(e.target.value);
                    setData([]);
                    setHasQueried(false);
                  }}
                >
                  {filters.type === "b2b" ? (
                    <>
                      <option value="bluepay">Bluepay</option>
                      <option value="kamino">Kamino</option>
                    </>
                  ) : (
                    <>
                      <option value="b2b">B2B</option>
                      <option value="b2c">B2C</option>
                      <option value="all">B2B e B2C</option>
                    </>
                  )}
                </Select>
              </Tooltip>
            </Box>
          )}

          <Box flex={1} minW="200px">
            <DebouncedAutocompleteInput
              placeholder="Promotora"
              tooltip="Filtrar por nome da promotora"
              inputValue={promoterInput}
              onInputChange={(val) => {
                setPromoterInput(val);
                onPromotoraSelect(null);
                setHasQueried(false);
              }}
              onSelect={(label, value) => {
                setPromoterInput(label);
                onPromotoraSelect({ label, value });
                setHasQueried(false);
              }}
              onClear={() => {
                setPromoterInput("");
                onPromotoraSelect(null);
                setHasQueried(false);
              }}
              fetchOptions={searchPromotoras}
            />
          </Box>

          <Box flex={1} minW="200px">
            <DebouncedAutocompleteInput
              placeholder="Corban"
              tooltip="Filtrar por nome do Corban"
              inputValue={corbanInput}
              onInputChange={(val) => {
                setCorbanInput(val);
                onCorbanSelect(null);
                setHasQueried(false);
              }}
              onSelect={(label, value) => {
                setCorbanInput(label);
                onCorbanSelect({ label, value });
                setHasQueried(false);
              }}
              onClear={() => {
                setCorbanInput("");
                onCorbanSelect(null);
                setHasQueried(false);
              }}
              fetchOptions={searchCorbans}
            />
          </Box>

          <Box flex={1} minW="200px">
            <DebouncedAutocompleteInput
              placeholder="Filtrar por Vendedor"
              tooltip="Filtrar por nome do Vendedor"
              inputValue={vendedorInput}
              onInputChange={(val) => {
                setVendedorInput(val);
                onVendedorSelect(null);
                setHasQueried(false);
              }}
              onSelect={(label, value) => {
                setVendedorInput(label);
                onVendedorSelect({ label, value });
                setHasQueried(false);
              }}
              onClear={() => {
                setVendedorInput("");
                onVendedorSelect(null);
                setHasQueried(false);
              }}
              fetchOptions={searchVendedores}
            />
          </Box>

          <Box flex={1} minW="200px">
            <DebouncedAutocompleteInput
              placeholder="Gerente Comercial"
              tooltip="Filtrar por Gerente Comercial"
              inputValue={gerenteInput}
              onInputChange={(val) => {
                setGerenteInput(val);
                onGerenteSelect(null);
                setHasQueried(false);
              }}
              onSelect={(label, value) => {
                setGerenteInput(label);
                onGerenteSelect({ label, value });
                setHasQueried(false);
              }}
              onClear={() => {
                setGerenteInput("");
                onGerenteSelect(null);
                setHasQueried(false);
              }}
              fetchOptions={searchGerentes}
            />
          </Box>
        </Flex>

        <Flex justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={4}>
          <Flex gap={3}>
            <Button
              variant="ghost"
              color="white"
              _hover={{ opacity: 0.8 }}
              onClick={onClear}
              height="2.8rem"
              px={6}
            >
              Limpar Filtros
            </Button>
            <Button
              bg="primary"
              color="white"
              _hover={{ opacity: 0.8 }}
              onClick={() => onQuery()}
              isLoading={isQuerying}
              isDisabled={hasQueried}
              borderRadius="lg"
              height="2.8rem"
              px={8}
              boxShadow="lg"
              fontWeight="bold"
            >
              Filtrar
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}
